import React, { createContext, useCallback, useContext, useEffect, useState, useMemo } from "react";
import { useRefState } from "../hooks/useRefState";
import { useEventContext } from "./EventContext";
import { useUserContext } from "./UserContext";
import { usePlayerContext } from "./PlayerContext";
import { useNotificationsContext } from "./NotificationsContext";
import { useStreamContext } from "./StreamContext";
import { useWidgetContext } from "./WidgetContextProvider";
import { commentGet, commentsGet } from "../../../cp/apiPlayer";
import { Event } from "../../../cp/components/Comet";
import { MODULE_COMMENTS } from "../components/SideModule/Tabs";
import { fullscreenClose } from "../components/Player/FullScreen";
import { DEFAULT_MESSAGES_MODEL, DEFAULT_SCROLL_MODEL, FULLSCREEN_MODES } from "../constants/common";
import { flattenMessages, deleteMessages, getMsgClientId } from "../helpers/messages-context";
import { uniqBy } from "../helpers/array";
import { MESSAGE_LIMIT } from "../constants/message-types";
import { checkTimeDifference } from "../helpers/times";

export const CommentsContext = createContext(null);
export const useCommentsContext = () => useContext(CommentsContext);

const getDefaultData = (streams, model) => streams.reduce((acc, stream) => {
    acc[stream?.commentRoom?.id] = model === 'message' ? DEFAULT_MESSAGES_MODEL : DEFAULT_SCROLL_MODEL;
    return acc;
}, {})

export const CommentsContextProvider = ({ children }) => {
    const { event } = useEventContext();
    const { widgetSettings } = useWidgetContext();
    const { stream, streams = [] } = useStreamContext();
    const { user } = useUserContext();
    const userId = user?.clientRegistrationId || user?.id;
    const { setFullScreen, setCurrentModule, tabletView, currentModule } = usePlayerContext();
    const { addMessage } = useNotificationsContext();

    const [data, setData] = useState(() => getDefaultData(streams, 'message'));
    const [scrollData, setScrollData] = useRefState(getDefaultData(streams, 'scroll'));
    const [scrollAtStart, setScrollAtStart] = useState(true);
    const [newMessages, setNewMessages] = useState([]);
    const [isLoading, setLoading] = useState(true);
    const [isLoadingMore, setLoadingMore] = useState(false);
    const [anonymous, setAnonymous] = useState(false);
    const [saveMessagesText, setSaveMessagesText] = useState({});

    const moduleEnabled = event.moduleComment.enabled;
    const enabledStreams = useMemo(() => streams
        .filter(str => !Array.isArray(widgetSettings?.allowCommentsStreamIds) || widgetSettings.allowCommentsStreamIds.includes(String(str.id)))
        .map(({ streamKey, commentRoom }) => ({ streamKey, roomId: commentRoom?.id, roomKey: commentRoom?.roomKey })), [streams, widgetSettings]);
    const { streamKey, commentRoom } = stream || {};
    const commentRooms = (streams || []).map(({ commentRoom }) => commentRoom);
    const roomId = commentRoom?.id;
    const roomKey = commentRoom?.roomKey;
    const eventKey = event?.eventKey;

    const { messages, hasMore } = data?.[roomId] || {};

    const filterVisible = (message) => (message.moderate || getMsgClientId(message) === userId);

    //Фильтруем сообщения и их потомков по признаку модерированности
    const visibleMessages = useMemo(() => (messages || [])
        .filter(filterVisible).map(message => ({ ...message, children: (message.children || []).filter(filterVisible) }))
        .sort((a, b) => a.time - b.time), [messages, roomId, userId]);

    //Разделяем закрепленные и обычные
    const regularMessages = visibleMessages.filter(({ fixed }) => !fixed);
    const fixedMessages = visibleMessages.filter(({ fixed }) => fixed).map((message) => {
        delete message.children;
        return message;
    });

    //Делаем одномерный массив и отделяем непрочитанные
    const flattenedMessages = useMemo(() => flattenMessages(regularMessages).reverse(), [regularMessages, userId]);
    const unreadMessages = flattenedMessages.filter(({ unread, fixed, time }) => {
        return unread && !fixed && checkTimeDifference(time, 2);
    });
    const unreadParentMessages = unreadMessages.filter(({ parentId }) => !parentId);
    const unreadChildrenMessages = unreadMessages.filter(({ parentId }) => parentId);

    // const updateCurrentData = (newData) => setData(data => ({ ...data, [roomId]: ({ ...data?.[roomId], ...newData }) }));

    const updateCurrentScrollData = (newData) => {
        setScrollAtStart(newData.scrollAtStart);
        setScrollData({ ...scrollData, [roomId]: ({ ...scrollData?.[roomId], ...newData }) })
    };

    const updateMessage = (message) => {
        setData(data => {
            if (!message) return data;

            const { roomId, parentId } = message;
            if (!data?.[roomId]) return data;

            let messages = data[roomId].messages || [];

            if (parentId) {
                const parent = messages.find(({ id }) => id === parentId);
                if (parent) {
                    parent.children = handleMessages(parent.children, message, parent);
                    messages = handleMessages(messages, parent);
                } else {
                    getParentMessageAndUpdate(message);
                }
            } else {
                messages = handleMessages(messages, message)
            }

            return { ...data, [roomId]: { ...data[roomId], messages } }

        })
    }

    const getMessages = useCallback(async (offset = 0, streams = []) => {
        if (!moduleEnabled) return;

        await Promise.all(streams.map(async ({ roomId, roomKey }) => {
            try {
                const params = {
                    offset,
                    limit: MESSAGE_LIMIT,
                    roomsKeys: [roomKey],
                }
                const comments = await commentsGet(eventKey, { ...params, moderate: true, });
                let commentsModerating = userId ? await commentsGet(eventKey, { ...params, moderate: false, limit: 10 }) : [];
                commentsModerating = commentsModerating.filter(msg => getMsgClientId(msg) === userId);

                setData(data => ({
                    ...data,
                    [roomId]: {
                        ...data?.[roomId],
                        messages: uniqBy([...data?.[roomId]?.messages || [], ...comments, ...commentsModerating], 'id'),
                        hasMore: comments.length === MESSAGE_LIMIT
                    }
                }));

            } catch (e) {
                console.error(e)
            }
        }))

        setTimeout(() => {
            setLoading(false);
            setLoadingMore(false);
        }, 500)

    }, [userId, moduleEnabled, eventKey]);

    const getMessagesMore = useCallback(() => {
        if (!messages) return;
        setLoadingMore(true);
        getMessages(messages.length, [{ roomId, roomKey, streamKey }]);
    }, [messages, roomId, roomKey, streamKey]);

    //Получаем родительское сообщение и добавляем к нему потомка, обновляем финальный массив
    const getParentMessageAndUpdate = async (message) => {
        if (!message) return;
        const _roomKey = commentRooms.find(({ id }) => message.roomId === id)?.roomKey;
        if (!_roomKey) return;
        const parentMessage = await commentGet(_roomKey, message.parentId);
        parentMessage.children = [...parentMessage.children, { ...message, unread: isChildMessageUnread(parentMessage, message) }];
        updateMessage(parentMessage);
    }

    useEffect(() => {
        if (!moduleEnabled) {
            setData(getDefaultData(streams))
            return;
        };
        //Запрашиваем данные для всех активных залов
        if (enabledStreams.some(stream => !data[stream?.roomId]?.messages)) {
            setLoading(true);
            getMessages(0, enabledStreams.filter(stream => !data[stream?.roomId]?.messages));
        }
    }, [eventKey, moduleEnabled, enabledStreams.length]);

    useEffect(() => {
        if (newMessages.length) {
            if (!isLoading) {
                //Обрабатываем все новые сообщения
                newMessages.forEach(message => updateMessage(message));
            }
            setNewMessages([]);
        }
    }, [newMessages, isLoading])

    const isMessageUnread = useCallback((message) => {
        return message && getMsgClientId(message) !== userId && (currentModule !== MODULE_COMMENTS || !scrollData[message.roomId]?.scrollAtStart);
    }, [userId, currentModule, scrollData]);

    const isChildMessageUnread = useCallback((parent, child) => {
        return parent && child && getMsgClientId(parent) === userId && getMsgClientId(child) !== userId
    }, [userId])

    const handleMessages = (messages, newMessage, parentMessage) => {
        if (!Array.isArray(messages)) return [];
        const isUnread = newMessage.parentId ? isChildMessageUnread(parentMessage, newMessage) : isMessageUnread(newMessage);
        const index = messages.findIndex((message) => message.id === newMessage.id);
        if (index < 0) {
            return [...messages, { ...newMessage, unread: isUnread }]; //Все новые сообщения считаются непрочитанными, если проходят проверку
        } else {
            return messages.map((message) => (message.id === newMessage.id ?
                {
                    ...message,
                    //При обновлении существующего сообщения оно может стать непрочитанным только если прошло модерацию
                    unread: !message.moderate && newMessage.moderate && isUnread,
                    ...newMessage,
                    children: uniqBy([...(message.children || []), ...(newMessage.children || [])], 'id'),
                }
                : message));
        }
    };

    const onReadAll = () => setData(data => data?.[roomId] ?
        ({ ...data, [roomId]: ({ ...data[roomId], messages: (data[roomId].messages || []).map(m => ({ ...m, unread: false })) }) })
        : data
    );

    const onMoveToQuestions = (message) => message && setData((data) => ({
        ...data,
        [roomId]: {
            ...(data?.[roomId] || {}),
            messages: data?.[roomId]?.messages?.filter(({ id }) => id !== message.id),
        }
    }));

    const onDeleteMessage = (message) => message &&
        setData(data =>
        ({
            ...data,
            [message.roomId]: {
                ...(data[message.roomId] || {}),
                messages: deleteMessages((data[message.roomId]?.messages || []), parseInt(message.id))
            }
        }));

    const onCreateFromQuestionMessage = (message) => {
        setNewMessages(messages => [...messages, message]);

        if (user && getMsgClientId(message) === userId) {
            const _message = {
                title: 'comments.createFromQuestion',
                buttonTitle: 'messages.look',
                callback: () => {
                    setFullScreen(fullScreen => {
                        if (fullScreen && !tabletView) {
                            return FULLSCREEN_MODES.CHAT;
                        }
                        if (fullScreen && tabletView) {
                            fullscreenClose();
                        }
                        return null;
                    });
                    setCurrentModule(MODULE_COMMENTS);
                },
                buttonRoute: '',
                pagesIsNotRoute: [`broadcast`, 'halls'],
            };
            addMessage(_message);
        }
    }

    const onChangeMessageText = (value = '') => setSaveMessagesText(saveMessagesText => ({ ...saveMessagesText, [roomId]: value }));

    const saveMessageText = useMemo(() => saveMessagesText[roomId], [saveMessagesText, roomId]);

    const context = {
        enabled: moduleEnabled && !!enabledStreams.find((str) => str.streamKey === streamKey),
        roomKey,
        isLoading,
        flattenedMessages: isLoading ? [] : flattenedMessages,
        fixedMessages,
        unreadMessages,
        unreadParentMessages,
        unreadChildrenMessages,
        hasMore,
        isLoadingMore,
        anonymous,
        setAnonymous,
        getMessagesMore,
        saveMessageText,
        onChangeMessageText,
        onReadAll,
        updateMessage,
        scrollData: scrollData?.[roomId] || {},
        scrollAtStart,
        updateScrollData: updateCurrentScrollData,
    };

    return (
        <CommentsContext.Provider value={context}>
            <Event event={'create:comment/message'} handler={(message) => setNewMessages(messages => [...messages, message])} />
            <Event event={'create-from-question:comment/message'} handler={(message) => onCreateFromQuestionMessage(message)} />
            <Event event={'create-from-comment:question/message'} handler={(message) => onMoveToQuestions(message)} />
            <Event event={'update:comment/message'} handler={(message) => setNewMessages(messages => [...messages, message])} />
            <Event event={'delete:comment/message'} handler={(message) => onDeleteMessage(message)} />
            {children}
        </CommentsContext.Provider>
    )
}
